import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../components/ui/select';
import {
  FileText, Plus, Trash2, Download, Search, Save, Eye, EyeOff, X, Upload, Loader2, Languages, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  draft:     { label: 'Brouillon', color: 'bg-zinc-700 text-zinc-300' },
  sent:      { label: 'Envoyé',    color: 'bg-amber-500/15 text-amber-300 border border-amber-500/30' },
  accepted:  { label: 'Accepté',   color: 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' },
  refused:   { label: 'Refusé',    color: 'bg-rose-500/15 text-rose-300 border border-rose-500/30' },
};

const empty = () => ({
  client_id: null,
  save_client: true,
  client_snapshot: { company_name: '', contact_name: '', email: '', phone: '', address: '', siret: '', is_pro: true },
  project_name: '',
  lines: [{ designation: '', quantity: 1, unit_price_ht: 0, visible_to_client: true }],
  margin_rate: 0,
  tva_rate: 20,
  delay_days: 21,
  clauses: [],
  notes: '',
});

export default function AdminQuotesPage() {
  const [tab, setTab] = useState('editor');
  const [clients, setClients] = useState([]);
  const [history, setHistory] = useState([]);
  const [search, setSearch] = useState('');
  const [defaults, setDefaults] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [translatingLine, setTranslatingLine] = useState({});
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(true);

  // Regenerate the PDF preview from the current form (without saving to DB)
  const refreshPreview = useCallback(async () => {
    if (!form.lines || form.lines.length === 0) {
      toast.error('Ajoutez au moins une ligne');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await api.post('/admin-quotes/preview', form, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      const raw = e?.response?.data;
      let msg = 'Erreur génération aperçu';
      if (raw instanceof Blob) {
        try { msg = JSON.parse(await raw.text())?.detail || msg; } catch { /* keep default */ }
      }
      toast.error(typeof msg === 'string' ? msg : 'Erreur aperçu');
    } finally {
      setPreviewLoading(false);
    }
  }, [form]);

  // Cleanup blob URL on unmount
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const translateLine = async (i) => {
    const src = (form.lines[i]?.designation || '').trim();
    if (!src) { toast.error('Ligne vide'); return; }
    setTranslatingLine(prev => ({ ...prev, [i]: true }));
    try {
      const { data } = await api.post('/admin-quotes/translate-line', { designation: src });
      const tr = (data?.translated || '').trim();
      if (tr && tr !== src) {
        updateLine(i, { designation: tr });
        toast.success('Traduit en français');
      } else {
        toast('Aucune traduction nécessaire');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erreur de traduction');
    } finally {
      setTranslatingLine(prev => ({ ...prev, [i]: false }));
    }
  };

  const handleAiImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';  // allow re-selecting the same file
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Format PDF requis');
      return;
    }
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/admin-quotes/parse-pdf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      // Hydrate the form with what the AI extracted — keep clauses + client + notes untouched.
      // `notes` is intentionally NOT set (backend forces it to "" and the field is no longer
      // rendered on the PDF — we don't want supplier / country / currency leaks).
      setForm(f => ({
        ...f,
        project_name: data.project_name || f.project_name,
        tva_rate: data.tva_rate ?? f.tva_rate,
        delay_days: data.delay_days ?? f.delay_days,
        lines: (data.lines && data.lines.length > 0) ? data.lines : f.lines,
      }));
      const extractedCount = (data.lines || []).length;
      const supplierTotal = data.total_ht_supplier ? ` · Total fournisseur HT lu : ${Number(data.total_ht_supplier).toFixed(2)} €` : '';
      toast.success(
        `IA : ${extractedCount} ligne(s) extraite(s) du devis${data.supplier_name ? ` de ${data.supplier_name}` : ''}.${supplierTotal} Pensez à vérifier les champs et ajouter votre marge.`,
        { duration: 7000 }
      );
    } catch (err) {
      const raw = err?.response?.data?.detail;
      toast.error(typeof raw === 'string' ? raw : 'Erreur extraction IA');
    } finally { setParsing(false); }
  };

  const fetchClients = useCallback(async () => {
    try { const { data } = await api.get('/clients'); setClients(data); } catch { /* ignore */ }
  }, []);
  const fetchHistory = useCallback(async () => {
    try { const { data } = await api.get('/admin-quotes'); setHistory(data); } catch { /* ignore */ }
  }, []);
  const fetchDefaults = useCallback(async () => {
    try {
      const { data } = await api.get('/admin-quotes/defaults');
      setDefaults(data);
      setForm(f => ({ ...f, clauses: f.clauses.length === 0 ? data.clauses : f.clauses }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchClients(); fetchHistory(); fetchDefaults(); }, [fetchClients, fetchHistory, fetchDefaults]);

  // ---- Computed totals (mirrors backend logic) ----
  const totals = (() => {
    const visible = (form.lines || []).filter(l => l.visible_to_client).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price_ht) || 0), 0);
    const hidden = (form.lines || []).filter(l => !l.visible_to_client).reduce((s, l) => s + (Number(l.quantity) || 0) * (Number(l.unit_price_ht) || 0), 0);
    const supplier_ht = visible + hidden;
    const margin_amount = supplier_ht * ((Number(form.margin_rate) || 0) / 100);
    const total_ht = supplier_ht + margin_amount;
    const total_tva = total_ht * ((Number(form.tva_rate) || 0) / 100);
    return {
      visible_ht: visible.toFixed(2),
      hidden_ht: hidden.toFixed(2),
      supplier_ht: supplier_ht.toFixed(2),
      margin_amount: margin_amount.toFixed(2),
      total_ht: total_ht.toFixed(2),
      total_tva: total_tva.toFixed(2),
      total_ttc: (total_ht + total_tva).toFixed(2),
    };
  })();

  const updateLine = (i, patch) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));
  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { designation: '', quantity: 1, unit_price_ht: 0, visible_to_client: true }] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const addClause = () => setForm(f => ({ ...f, clauses: [...f.clauses, ''] }));
  const updateClause = (i, val) => setForm(f => ({ ...f, clauses: f.clauses.map((c, idx) => idx === i ? val : c) }));
  const removeClause = (i) => setForm(f => ({ ...f, clauses: f.clauses.filter((_, idx) => idx !== i) }));

  const pickClient = (id) => {
    const c = clients.find(x => x.id === id);
    if (c) {
      setForm(f => ({
        ...f,
        client_id: id,
        save_client: false,
        client_snapshot: {
          company_name: c.company_name || '',
          contact_name: c.contact_name || '',
          email: c.email || '',
          phone: c.phone || '',
          address: c.address || '',
          siret: c.siret || '',
          is_pro: !!c.siret,
        },
      }));
    }
  };

  const save = async () => {
    if (!form.client_snapshot.company_name && !form.client_snapshot.contact_name) {
      toast.error('Nom du client ou de l\'entreprise requis');
      return;
    }
    if (form.lines.length === 0 || !form.lines.some(l => l.designation && l.unit_price_ht > 0)) {
      toast.error('Ajoutez au moins une ligne avec désignation et prix');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post('/admin-quotes', form);
      toast.success(`Devis ${data.quote_number} créé`);
      setForm(empty());
      setForm(f => ({ ...f, clauses: defaults?.clauses || [] }));
      fetchHistory(); fetchClients();
      setTab('history');
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = 'Erreur';
      if (typeof raw === 'string') msg = raw;
      else if (Array.isArray(raw)) msg = raw.map(x => x?.msg || JSON.stringify(x)).join(' · ');
      toast.error(msg);
    } finally { setSaving(false); }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/admin-quotes/${q.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${q.quote_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Erreur génération PDF'); }
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Supprimer le devis ${q.quote_number} ?`)) return;
    try { await api.delete(`/admin-quotes/${q.id}`); toast.success('Supprimé'); fetchHistory(); }
    catch { toast.error('Erreur'); }
  };

  const filteredHistory = history.filter(q => {
    if (!search.trim()) return true;
    const s = search.trim().toLowerCase();
    return (q.quote_number || '').toLowerCase().includes(s)
      || (q.client_snapshot?.company_name || '').toLowerCase().includes(s)
      || (q.client_snapshot?.contact_name || '').toLowerCase().includes(s)
      || (q.project_name || '').toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-quotes-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-zinc-50 tracking-tight flex items-center gap-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
          <FileText className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400" /> Devis Client
        </h1>
        <p className="text-xs sm:text-sm text-zinc-500 mt-1">
          Éditeur de devis professionnel. Génération PDF auto-numérotée (DEV-YYYY-XXXX) + historique.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 rounded-sm h-auto p-1">
          <TabsTrigger value="editor" className="data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-950 rounded-sm text-xs px-3 py-1.5" data-testid="tab-editor">Nouveau devis</TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-amber-500 data-[state=active]:text-zinc-950 rounded-sm text-xs px-3 py-1.5" data-testid="tab-history">Historique ({history.length})</TabsTrigger>
        </TabsList>

        {/* ============== EDITOR ============== */}
        <TabsContent value="editor" className="mt-4 space-y-4">
          {/* AI import banner — uploads the subcontractor PDF and prefills the form */}
          <Card className="bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-transparent border-amber-500/30 rounded-sm" data-testid="ai-import-card">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-amber-200 flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Pré-remplissage IA depuis un PDF
                </h3>
                <p className="text-[11px] text-zinc-400 mt-0.5">
                  Uploadez le devis de votre sous-traitant : l'IA extrait projet, lignes, prix, TVA et délai. Vous ajoutez ensuite votre marge et vérifiez avant d'enregistrer.
                </p>
              </div>
              <label className="cursor-pointer shrink-0">
                <input type="file" accept="application/pdf,.pdf" className="hidden" onChange={handleAiImport} disabled={parsing} data-testid="ai-import-file" />
                <span className={`inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium ${parsing ? 'bg-zinc-800 text-zinc-500 cursor-wait' : 'bg-amber-500 text-zinc-950 hover:bg-amber-400'}`}>
                  {parsing ? <><Loader2 className="w-4 h-4 animate-spin" /> Analyse en cours…</> : <><Upload className="w-4 h-4" /> Importer un PDF</>}
                </span>
              </label>
            </CardContent>
          </Card>

          {/* PDF preview panel — regenerated on demand from the current form (no DB write) */}
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm" data-testid="preview-card">
            <CardContent className="p-3">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPreviewOpen(o => !o)}
                    className="flex items-center gap-2 text-sm font-semibold text-zinc-100 hover:text-amber-300"
                    data-testid="preview-toggle"
                  >
                    <FileText className="w-4 h-4 text-amber-400" />
                    Aperçu du devis client
                    <span className="text-[10px] text-zinc-500 font-normal">{previewOpen ? '▲ masquer' : '▼ afficher'}</span>
                  </button>
                  <span className="text-[11px] text-zinc-500 hidden sm:inline">Contrôlez le rendu avant validation</span>
                </div>
                <Button
                  size="sm"
                  onClick={refreshPreview}
                  disabled={previewLoading}
                  className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40 rounded-sm h-7 text-xs"
                  data-testid="refresh-preview-btn"
                >
                  {previewLoading ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Génération…</> : <><RefreshCw className="w-3 h-3 mr-1" /> Actualiser l'aperçu</>}
                </Button>
              </div>
              {previewOpen && (
                <div className="w-full bg-zinc-950 border border-zinc-800 rounded-sm overflow-hidden" style={{ height: '700px' }} data-testid="preview-frame-wrap">
                  {previewUrl ? (
                    <iframe
                      key={previewUrl}
                      src={previewUrl}
                      title="Aperçu devis"
                      className="w-full h-full"
                      data-testid="preview-iframe"
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center text-center gap-3 text-zinc-500 p-6">
                      <FileText className="w-12 h-12 text-zinc-800" />
                      <p className="text-sm">Cliquez sur « Actualiser l'aperçu » pour générer le PDF client à partir des données actuelles.</p>
                      <p className="text-[11px] text-zinc-600">L'aperçu ne crée pas de devis dans l'historique — c'est un rendu temporaire.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Client section */}
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-zinc-100">Client</h3>
                <Select value={form.client_id || 'new'} onValueChange={(v) => v === 'new' ? setForm(f => ({ ...f, client_id: null })) : pickClient(v)}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-200 rounded-sm w-64 h-8 text-xs" data-testid="client-picker"><SelectValue placeholder="Sélectionner client existant…" /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-200 max-h-64">
                    <SelectItem value="new">— Nouveau client —</SelectItem>
                    {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name} {c.contact_name && `(${c.contact_name})`}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label className="text-xs text-zinc-400">Entreprise</Label><Input data-testid="client-company" value={form.client_snapshot.company_name} onChange={(e) => setForm(f => ({ ...f, client_snapshot: { ...f.client_snapshot, company_name: e.target.value } }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" /></div>
                <div><Label className="text-xs text-zinc-400">Contact (Prénom Nom)</Label><Input data-testid="client-contact" value={form.client_snapshot.contact_name} onChange={(e) => setForm(f => ({ ...f, client_snapshot: { ...f.client_snapshot, contact_name: e.target.value } }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" /></div>
                <div><Label className="text-xs text-zinc-400">Email</Label><Input value={form.client_snapshot.email} onChange={(e) => setForm(f => ({ ...f, client_snapshot: { ...f.client_snapshot, email: e.target.value } }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" /></div>
                <div><Label className="text-xs text-zinc-400">Téléphone</Label><Input value={form.client_snapshot.phone} onChange={(e) => setForm(f => ({ ...f, client_snapshot: { ...f.client_snapshot, phone: e.target.value } }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" /></div>
                <div className="sm:col-span-2"><Label className="text-xs text-zinc-400">Adresse</Label><Textarea rows={2} value={form.client_snapshot.address} onChange={(e) => setForm(f => ({ ...f, client_snapshot: { ...f.client_snapshot, address: e.target.value } }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 text-sm" /></div>
                <div><Label className="text-xs text-zinc-400">SIRET (si pro)</Label><Input value={form.client_snapshot.siret} onChange={(e) => setForm(f => ({ ...f, client_snapshot: { ...f.client_snapshot, siret: e.target.value, is_pro: !!e.target.value } }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" /></div>
                {!form.client_id && (
                  <div className="flex items-center gap-2 self-end">
                    <Checkbox checked={form.save_client} onCheckedChange={(v) => setForm(f => ({ ...f, save_client: !!v }))} data-testid="save-client-toggle" />
                    <Label className="text-xs text-zinc-400 cursor-pointer">Sauvegarder le client pour réutilisation</Label>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Project + lines */}
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-4 space-y-3">
              <div>
                <Label className="text-xs text-zinc-400">Projet / Référence</Label>
                <Input data-testid="project-name" value={form.project_name} onChange={(e) => setForm(f => ({ ...f, project_name: e.target.value }))} placeholder="Ex: Production série 50 pièces brides alu 7075" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" />
              </div>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-zinc-100">Lignes du devis</h3>
                  <Button size="sm" onClick={addLine} className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-sm h-7 text-xs" data-testid="add-line">
                    <Plus className="w-3 h-3 mr-1" /> Ajouter ligne
                  </Button>
                </div>
                <div className="space-y-2">
                  {form.lines.map((l, i) => (
                    <div key={i} className={`grid grid-cols-12 gap-2 items-center p-2 rounded-sm border ${l.visible_to_client ? 'bg-zinc-950 border-zinc-800' : 'bg-zinc-800/30 border-zinc-700 border-dashed'}`} data-testid={`line-${i}`}>
                      <Input className="col-span-12 sm:col-span-6 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm" placeholder="Désignation" value={l.designation} onChange={(e) => updateLine(i, { designation: e.target.value })} />
                      <Input className="col-span-4 sm:col-span-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm text-right" type="number" step="0.01" placeholder="Qté" value={l.quantity} onChange={(e) => updateLine(i, { quantity: e.target.value })} />
                      <Input className="col-span-5 sm:col-span-2 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm h-8 text-sm text-right" type="number" step="0.01" placeholder="P.U. HT" value={l.unit_price_ht} onChange={(e) => updateLine(i, { unit_price_ht: e.target.value })} />
                      <div className="col-span-3 sm:col-span-2 text-right text-xs text-zinc-300 font-mono">
                        {((Number(l.quantity)||0) * (Number(l.unit_price_ht)||0)).toFixed(2)} €
                      </div>
                      <div className="col-span-12 sm:col-span-1 flex items-center justify-end gap-1">
                        <button
                          onClick={() => translateLine(i)}
                          title="Traduire cette ligne en français"
                          disabled={translatingLine[i]}
                          className="p-1 text-sky-400 hover:text-sky-300 disabled:opacity-50"
                          data-testid={`translate-line-${i}`}
                        >
                          {translatingLine[i] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
                        </button>
                        <button onClick={() => updateLine(i, { visible_to_client: !l.visible_to_client })} title={l.visible_to_client ? 'Visible au client' : 'CACHÉE — incluse dans le total mais pas listée sur le PDF'} className={`p-1 ${l.visible_to_client ? 'text-emerald-400 hover:text-emerald-300' : 'text-rose-400 hover:text-rose-300'}`} data-testid={`toggle-visible-${i}`}>
                          {l.visible_to_client ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                        </button>
                        <button onClick={() => removeLine(i)} title="Supprimer" className="p-1 text-zinc-500 hover:text-rose-400" data-testid={`remove-line-${i}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-zinc-500 mt-2">
                  💡 <strong>Œil barré</strong> = ligne cachée au client. Son montant est inclus dans le total HT/TTC du PDF mais elle ne sera pas listée.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Totaux + délai + TVA + Marge */}
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Marge (%)</Label>
                <Input data-testid="margin-rate" type="number" step="0.1" value={form.margin_rate} onChange={(e) => setForm(f => ({ ...f, margin_rate: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" placeholder="0" />
                <p className="text-[10px] text-zinc-500 mt-1">Appliquée sur le HT fournisseur avant TVA</p>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">TVA (%)</Label>
                <Input data-testid="tva-rate" type="number" step="0.1" value={form.tva_rate} onChange={(e) => setForm(f => ({ ...f, tva_rate: e.target.value }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Délai production (j.ouvrés)</Label>
                <Input data-testid="delay-days" type="number" min="1" value={form.delay_days} onChange={(e) => setForm(f => ({ ...f, delay_days: parseInt(e.target.value, 10) || 0 }))} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm mt-1 h-8 text-sm" />
              </div>
              <div className="bg-amber-500/5 border border-amber-500/20 rounded-sm p-3 text-xs space-y-1" data-testid="totals-block">
                <div className="flex justify-between text-zinc-400"><span>HT fournisseur</span><span className="font-mono">{totals.supplier_ht} €</span></div>
                {Number(form.margin_rate) > 0 && (
                  <div className="flex justify-between text-sky-300"><span>Marge ({form.margin_rate}%)</span><span className="font-mono">+{totals.margin_amount} €</span></div>
                )}
                <div className="flex justify-between text-zinc-200 border-t border-zinc-800 pt-1 mt-1"><span>Total HT</span><span className="font-mono font-semibold">{totals.total_ht} €</span></div>
                <div className="flex justify-between text-zinc-400"><span>TVA</span><span className="font-mono">{totals.total_tva} €</span></div>
                <div className="flex justify-between text-amber-300 text-sm font-bold border-t border-amber-500/30 pt-1 mt-1"><span>Total TTC</span><span className="font-mono">{totals.total_ttc} €</span></div>
              </div>
            </CardContent>
          </Card>

          {/* Clauses */}
          <Card className="bg-zinc-900 border-zinc-800 rounded-sm">
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-zinc-100">Conditions / Clauses</h3>
                <Button size="sm" onClick={addClause} className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 rounded-sm h-7 text-xs" data-testid="add-clause"><Plus className="w-3 h-3 mr-1" /> Ajouter</Button>
              </div>
              {form.clauses.map((c, i) => (
                <div key={i} className="flex items-start gap-2" data-testid={`clause-${i}`}>
                  <Textarea rows={2} value={c} onChange={(e) => updateClause(i, e.target.value)} className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm text-xs flex-1" />
                  <button onClick={() => removeClause(i)} className="p-1.5 text-zinc-500 hover:text-rose-400 mt-1" title="Supprimer">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm font-medium" data-testid="save-quote-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Enregistrement…' : 'Créer le devis'}
            </Button>
          </div>
        </TabsContent>

        {/* ============== HISTORY ============== */}
        <TabsContent value="history" className="mt-4 space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher par numéro, client, projet…" className="bg-zinc-950 border-zinc-800 text-zinc-100 rounded-sm pl-9 h-9 text-sm" data-testid="history-search" />
          </div>
          {filteredHistory.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800 rounded-sm"><CardContent className="p-8 text-center text-zinc-500 text-sm">Aucun devis enregistré pour le moment.</CardContent></Card>
          ) : (
            filteredHistory.map(q => {
              const st = STATUS_LABEL[q.status] || STATUS_LABEL.draft;
              return (
                <Card key={q.id} className="bg-zinc-900 border-zinc-800 rounded-sm hover:border-zinc-700" data-testid={`quote-row-${q.id}`}>
                  <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-amber-400 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-amber-300 text-sm font-semibold">{q.quote_number}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${st.color}`}>{st.label}</span>
                        </div>
                        <p className="text-sm text-zinc-200 truncate">{q.client_snapshot?.company_name || q.client_snapshot?.contact_name || '—'} {q.project_name && <span className="text-zinc-500">· {q.project_name}</span>}</p>
                        <p className="text-[10px] text-zinc-600">{new Date(q.created_at).toLocaleDateString('fr-FR')} · Total TTC <strong className="text-zinc-300 font-mono">{Number(q.totals?.total_ttc || 0).toFixed(2)} €</strong></p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="sm" onClick={() => downloadPdf(q)} className="bg-amber-500 text-zinc-950 hover:bg-amber-400 rounded-sm h-7 text-xs" data-testid={`download-pdf-${q.id}`}>
                        <Download className="w-3 h-3 mr-1" /> PDF
                      </Button>
                      <button onClick={() => deleteQuote(q)} className="p-1.5 text-zinc-500 hover:text-rose-400" data-testid={`delete-quote-${q.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
